import { z } from 'zod';
import { timestampSchema } from './admin.js';
import { visibilitySchema, type ProjectPermission } from './permissions.js';
import {
  isoDateSchema,
  labelSchema,
  personSchema,
  projectIdParamsSchema,
  statusCategorySchema,
  type StatusCategory,
} from './projects.js';
import { richTextSchema } from './rich-text.js';

/** TASK-01..08: tasks and subtasks, detail view, moving between projects, trash. */

export const taskPrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type TaskPriority = z.infer<typeof taskPrioritySchema>;

export const taskTypeSchema = z.enum(['task', 'milestone']);
export type TaskType = z.infer<typeof taskTypeSchema>;

/** GANTT-04: end–start, start–start, end–end, start–end. */
export const dependencyTypeSchema = z.enum(['FS', 'SS', 'FF', 'SF']);

/** Problem `type` URIs of tasks, so the UI can show specific messages. */
export const taskProblemTypes = {
  invalidParent: 'urn:pm-tool:problem:task-invalid-parent',
  milestoneSubtasks: 'urn:pm-tool:problem:task-milestone-subtasks',
  invalidStatus: 'urn:pm-tool:problem:task-invalid-status',
  invalidAssignee: 'urn:pm-tool:problem:task-invalid-assignee',
  invalidLabel: 'urn:pm-tool:problem:task-invalid-label',
  datesUnordered: 'urn:pm-tool:problem:task-dates-unordered',
  fieldForbidden: 'urn:pm-tool:problem:task-field-forbidden',
  invalidMoveTarget: 'urn:pm-tool:problem:task-invalid-move-target',
  trashExpired: 'urn:pm-tool:problem:task-trash-expired',
  parentDeleted: 'urn:pm-tool:problem:task-parent-deleted',
  invalidNeighbor: 'urn:pm-tool:problem:task-invalid-neighbor',
  /** Dates or progress of a task with subtasks are derived from them (decision 6b). */
  derivedField: 'urn:pm-tool:problem:task-derived-field',
  /** Subtasks under a task with dependencies (dependencies only on tasks without subtasks). */
  parentHasDependencies: 'urn:pm-tool:problem:task-parent-has-dependencies',
} as const;

// --- Keys (TASK-02, TASK-07) -------------------------------------------------------------------

/** `WEB-123`; lower case is accepted and normalized. */
export const taskKeySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,6}-[1-9][0-9]{0,8}$/, 'Schlüssel wie WEB-123');

export function formatTaskKey(projectKey: string, number: number): string {
  return `${projectKey}-${String(number)}`;
}

export function parseTaskKey(key: string): { projectKey: string; number: number } | null {
  const parsed = taskKeySchema.safeParse(key);
  if (!parsed.success) return null;
  const [projectKey = '', number = ''] = parsed.data.split('-');
  return { projectKey, number: Number(number) };
}

// --- Trash (TASK-08) ---------------------------------------------------------------------------

/** Deleted tasks can be restored for this many days; afterwards they are purged (phase 5 job). */
export const TRASH_RETENTION_DAYS = 30;

export function isRestorable(deletedAt: Date, now: Date): boolean {
  return now.getTime() - deletedAt.getTime() < TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;
}

// --- Moving (TASK-07) --------------------------------------------------------------------------

/**
 * Status of a moved task in the target project: same name (ignoring case), else the first status
 * of the same category, else the first status. `targetStatuses` in workflow order, not empty.
 */
export function mapStatusForMove(
  source: { name: string; category: StatusCategory },
  targetStatuses: readonly { id: string; name: string; category: StatusCategory }[],
): string {
  const name = source.name.toLocaleLowerCase('de');
  const match =
    targetStatuses.find((s) => s.name.toLocaleLowerCase('de') === name) ??
    targetStatuses.find((s) => s.category === source.category) ??
    targetStatuses[0];
  if (match === undefined) throw new Error('target project has no statuses');
  return match.id;
}

/**
 * Moving into a project of another customer organization would show the task to that customer;
 * it becomes internal then (decision phase 3b). Projects without organization keep it.
 */
export function resetsVisibilityOnMove(
  sourceOrganizationId: string | null,
  targetOrganizationId: string | null,
): boolean {
  return targetOrganizationId !== null && targetOrganizationId !== sourceOrganizationId;
}

// --- Schemas -----------------------------------------------------------------------------------

export const taskTitleSchema = z.string().trim().min(1).max(300);
/** Stored as numeric(6,2), i.e. rounded to two decimals. */
export const estimateHoursSchema = z.number().min(0).max(9999.99);
export const progressSchema = z.number().int().min(0).max(100);

export const taskStatusRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  category: statusCategorySchema,
  color: z.string(),
});

export const taskSummarySchema = z.object({
  id: z.uuid(),
  key: z.string(),
  parentId: z.uuid().nullable(),
  type: taskTypeSchema,
  title: z.string(),
  status: taskStatusRefSchema,
  priority: taskPrioritySchema,
  visibility: visibilitySchema,
  /** TASK-03: at most one person is responsible (E-37). */
  assignee: personSchema.nullable(),
  labels: z.array(labelSchema),
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
  /** Subtasks the viewer can see. */
  subtaskCount: z.object({ done: z.number().int(), total: z.number().int() }),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type TaskSummary = z.infer<typeof taskSummarySchema>;

/** A dependency seen from one of its tasks: the other task, type and lag (GANTT-04). */
export const taskDependencyRefSchema = z.object({
  id: z.uuid(),
  task: z.object({ id: z.uuid(), key: z.string(), title: z.string(), type: taskTypeSchema }),
  type: dependencyTypeSchema,
  lagDays: z.number().int(),
});
export type TaskDependencyRef = z.infer<typeof taskDependencyRefSchema>;

export const taskSchema = taskSummarySchema.extend({
  project: z.object({ id: z.uuid(), key: z.string(), name: z.string() }),
  parent: z.object({ id: z.uuid(), key: z.string(), title: z.string() }).nullable(),
  description: richTextSchema.nullable(),
  estimateHours: z.number().nullable(),
  progress: z.number().int(),
  /** Decision 6b: dates come from the subtasks (at least one subtask has a date). */
  datesDerived: z.boolean(),
  /** Decision 6b: progress comes from the subtasks (the task has subtasks). */
  progressDerived: z.boolean(),
  /** Dependencies to visible tasks (GANTT-04). */
  predecessors: z.array(taskDependencyRefSchema),
  successors: z.array(taskDependencyRefSchema),
  watchers: z.array(personSchema),
  /** Whether the viewer watches the task. */
  watching: z.boolean(),
  subtasks: z.array(taskSummarySchema),
  createdBy: personSchema.nullable(),
  completedAt: timestampSchema.nullable(),
});
export type Task = z.infer<typeof taskSchema>;

export const taskIdParamsSchema = z.object({ taskId: z.uuid() });
export const taskKeyParamsSchema = z.object({ key: taskKeySchema });
export const projectTasksParamsSchema = projectIdParamsSchema;

/**
 * TASK-04: the whole filtered and sorted list (decision 3c: no cursor, at most TASK_LIST_LIMIT
 * tasks; grouping and virtualization happen in the browser). Filters: task-list.ts.
 */
export const TASK_LIST_LIMIT = 2000;
export const taskListResponseSchema = z.object({
  items: z.array(taskSummarySchema),
  /** More tasks match than TASK_LIST_LIMIT. */
  truncated: z.boolean(),
  /** With `counts`: visible tasks per status id, regardless of the filters (KAN-05). */
  statusCounts: z.record(z.string(), z.number().int()).optional(),
});
export type TaskListResponse = z.infer<typeof taskListResponseSchema>;

function datesOrdered(v: {
  startDate?: string | null | undefined;
  dueDate?: string | null | undefined;
}) {
  return v.startDate == null || v.dueDate == null || v.startDate <= v.dueDate;
}
const datesOrderedIssue = { message: 'Start date after due date', path: ['dueDate'] };

const uniqueIds = z
  .array(z.uuid())
  .max(50)
  .transform((ids) => [...new Set(ids)]);

/**
 * Rejects the field of the n:m assignees (until E-37) instead of silently dropping it, so an
 * outdated client gets an error rather than losing the assignment.
 */
const noOldAssignees = z
  .never({ error: 'One assignee only: use assigneeId (E-37)' })
  .optional()
  .meta({ deprecated: true });

/**
 * New task or subtask (`parentId`). Visibility defaults to the parent's or the project default;
 * external users always create external tasks. Milestones take `dueDate` as their only date.
 */
export const createTaskBodySchema = z
  .object({
    title: taskTitleSchema,
    description: richTextSchema.nullable().optional(),
    parentId: z.uuid().nullable().optional(),
    type: taskTypeSchema.default('task'),
    /** Defaults to the first status of the workflow. */
    statusId: z.uuid().optional(),
    priority: taskPrioritySchema.default('medium'),
    assigneeId: z.uuid().nullable().optional(),
    assigneeIds: noOldAssignees,
    labelIds: uniqueIds.optional(),
    startDate: isoDateSchema.nullable().optional(),
    dueDate: isoDateSchema.nullable().optional(),
    estimateHours: estimateHoursSchema.nullable().optional(),
    visibility: visibilitySchema.optional(),
  })
  .refine(datesOrdered, datesOrderedIssue);
export type CreateTaskBody = Omit<z.infer<typeof createTaskBodySchema>, 'assigneeIds'>;

/** Date order is checked against the stored values in the service as well. */
export const updateTaskBodySchema = z
  .object({
    title: taskTitleSchema.optional(),
    description: richTextSchema.nullable().optional(),
    type: taskTypeSchema.optional(),
    statusId: z.uuid().optional(),
    priority: taskPrioritySchema.optional(),
    /** null removes the assignee. */
    assigneeId: z.uuid().nullable().optional(),
    assigneeIds: noOldAssignees,
    labelIds: uniqueIds.optional(),
    startDate: isoDateSchema.nullable().optional(),
    dueDate: isoDateSchema.nullable().optional(),
    estimateHours: estimateHoursSchema.nullable().optional(),
    /** GANTT-08: only tasks without subtasks (derived otherwise). */
    progress: progressSchema.optional(),
    visibility: visibilitySchema.optional(),
  })
  .refine(datesOrdered, datesOrderedIssue);
export type UpdateTaskBody = Omit<z.infer<typeof updateTaskBodySchema>, 'assigneeIds'>;

/**
 * Permissions per field of an update; any one of them suffices. `task.edit_status` allows only
 * the status (e.g. approval by the customer).
 */
export const taskFieldPermissions: Record<keyof UpdateTaskBody, readonly ProjectPermission[]> = {
  title: ['task.edit'],
  description: ['task.edit'],
  type: ['task.edit'],
  statusId: ['task.edit', 'task.edit_status'],
  priority: ['task.edit'],
  assigneeId: ['task.assign'],
  labelIds: ['task.edit'],
  // Decision 6b: dates also with gantt.edit (planning without editing the content).
  startDate: ['task.edit', 'gantt.edit'],
  dueDate: ['task.edit', 'gantt.edit'],
  estimateHours: ['task.edit'],
  progress: ['task.edit'],
  visibility: ['task.set_visibility'],
};

export function canEditTaskField(
  permissions: ReadonlySet<ProjectPermission>,
  field: keyof UpdateTaskBody,
): boolean {
  return taskFieldPermissions[field].some((p) => permissions.has(p));
}

/** Fields of `body` the permissions do not allow. */
export function forbiddenTaskFields(
  permissions: ReadonlySet<ProjectPermission>,
  body: UpdateTaskBody,
): (keyof UpdateTaskBody)[] {
  return (Object.keys(taskFieldPermissions) as (keyof UpdateTaskBody)[]).filter(
    (field) => body[field] !== undefined && !canEditTaskField(permissions, field),
  ); // keys of a const record
}

export const moveTaskBodySchema = z.object({
  targetProjectId: z.uuid(),
  /** Defaults to the status mapped by `mapStatusForMove`. */
  statusId: z.uuid().optional(),
});
export type MoveTaskBody = z.infer<typeof moveTaskBodySchema>;

export const moveTaskResponseSchema = z.object({
  task: taskSchema,
  /** What did not survive the move: labels without a namesake, people without access. */
  dropped: z.object({
    labels: z.array(z.string()),
    /** Tasks of the tree whose assignee has no access in the target project. */
    assignees: z.number().int(),
    watchers: z.number().int(),
  }),
  /** The task (and its subtasks) became internal because the customer organization changed. */
  visibilityReset: z.boolean(),
});
export type MoveTaskResponse = z.infer<typeof moveTaskResponseSchema>;

export const moveTargetsResponseSchema = z.object({
  items: z.array(
    z.object({
      id: z.uuid(),
      key: z.string(),
      name: z.string(),
      resetsVisibility: z.boolean(),
      statuses: z.array(taskStatusRefSchema),
    }),
  ),
});
export type MoveTargetsResponse = z.infer<typeof moveTargetsResponseSchema>;

export const trashItemSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  title: z.string(),
  type: taskTypeSchema,
  visibility: visibilitySchema,
  /** Set for subtasks deleted on their own (their parent still exists). */
  parentKey: z.string().nullable(),
  /** Subtasks deleted together with this task. */
  deletedSubtasks: z.number().int(),
  deletedAt: timestampSchema,
  /** End of the restore window (TRASH_RETENTION_DAYS). */
  restorableUntil: timestampSchema,
});
export type TrashItem = z.infer<typeof trashItemSchema>;

export const trashResponseSchema = z.object({ items: z.array(trashItemSchema) });
export type TrashResponse = z.infer<typeof trashResponseSchema>;
